import type {
  OpenAnalystClient,
  PluginCatalogResponse,
  PluginInstalledResponse,
} from "@openanalyst-ai/sdk/v2/client"
import { canonicalPluginAppID } from "@/plugins/runtime"

export type InstalledPlugin = PluginInstalledResponse[number]
export type CatalogItem = PluginCatalogResponse["items"][number]
export type PluginInstallScope = "project" | "global"

export const PLUGIN_RUNTIME_RELOADED_EVENT = "openanalyst:plugins-reloaded"
export const PLUGIN_RESTART_INTENT_STORAGE_KEY = "openanalyst.restartIntent.v1"

export type PluginRestartIntent = {
  path: string
  dialog: "settings"
  tab: "plugins"
}

type PluginRestartIntentStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">

function defaultPluginRestartStorage() {
  if (typeof window === "undefined") return
  return window.localStorage
}

export function normalizePluginRestartPath(path: string) {
  const value = path.trim()
  if (!value.startsWith("/") || value.startsWith("//")) return

  try {
    const base = "https://openanalyst.local"
    const url = new URL(value, base)
    if (url.origin !== base) return
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return
  }
}

export function savePluginRestartIntent(input: { path: string; storage?: Pick<Storage, "setItem"> }) {
  const path = normalizePluginRestartPath(input.path)
  const storage = input.storage ?? defaultPluginRestartStorage()
  if (!path || !storage) return false

  try {
    storage.setItem(
      PLUGIN_RESTART_INTENT_STORAGE_KEY,
      JSON.stringify({
        path,
        dialog: "settings",
        tab: "plugins",
      } satisfies PluginRestartIntent),
    )
    return true
  } catch {
    return false
  }
}

export function clearPluginRestartIntent(input?: { storage?: Pick<Storage, "removeItem"> }) {
  const storage = input?.storage ?? defaultPluginRestartStorage()
  if (!storage) return

  try {
    storage.removeItem(PLUGIN_RESTART_INTENT_STORAGE_KEY)
  } catch {
    return
  }
}

export function readPluginRestartIntent(input?: { storage?: Pick<PluginRestartIntentStorage, "getItem" | "removeItem"> }) {
  const storage = input?.storage ?? defaultPluginRestartStorage()
  if (!storage) return

  try {
    const raw = storage.getItem(PLUGIN_RESTART_INTENT_STORAGE_KEY)
    if (!raw) return

    const parsed = JSON.parse(raw) as Partial<PluginRestartIntent>
    const path = typeof parsed.path === "string" ? normalizePluginRestartPath(parsed.path) : undefined
    if (!path || parsed.dialog !== "settings" || parsed.tab !== "plugins") {
      clearPluginRestartIntent({ storage })
      return
    }

    return {
      path,
      dialog: "settings",
      tab: "plugins",
    } satisfies PluginRestartIntent
  } catch {
    clearPluginRestartIntent({ storage })
    return
  }
}

export function notifyPluginRuntimeReloaded(input: {
  directory: string
  scope?: PluginInstallScope
  pluginID?: string
  enabled?: boolean
}) {
  if (typeof window === "undefined") return
  window.dispatchEvent(
    new CustomEvent(PLUGIN_RUNTIME_RELOADED_EVENT, {
      detail: {
        directory: input.directory,
        scope: input.scope ?? "project",
        ...(input.pluginID ? { pluginID: input.pluginID } : {}),
        ...(typeof input.enabled === "boolean" ? { enabled: input.enabled } : {}),
      },
    }),
  )
}

export function pluginRuntimeReloadMatches(event: Event, directory: string) {
  const detail =
    typeof CustomEvent !== "undefined" && event instanceof CustomEvent && event.detail && typeof event.detail === "object"
      ? (event.detail as { directory?: unknown; scope?: unknown })
      : {}
  return detail.scope === "global" || detail.directory === directory
}

export function createPluginClient(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
}) {
  return input.createClient({
    directory: input.directory,
    throwOnError: true,
  })
}

export function normalizePluginSpec(spec: string) {
  const value = spec.trim()
  if (!value || value.startsWith("file:") || value.startsWith(".") || value.startsWith("/") || value.startsWith("git:")) {
    return value
  }
  if (value.startsWith("@")) {
    const index = value.indexOf("@", 1)
    return index > 0 ? value.slice(0, index) : value
  }
  const index = value.indexOf("@")
  return index > 0 ? value.slice(0, index) : value
}

export function pluginMatchesCatalog(item: InstalledPlugin, catalog: CatalogItem) {
  return item.spec === catalog.spec || item.normalized === normalizePluginSpec(catalog.spec)
}

export function catalogUnavailableReason(item: CatalogItem) {
  if (item.status === "blocked") return "blocked"
  if (item.revokeStatus?.revoked) return "revoked"
  if (item.eligibility?.eligible === false) return "unavailable"
}

export async function loadInstalledPlugins(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
}) {
  const result = await createPluginClient(input).plugin.installed({ directory: input.directory })
  return result.data ?? []
}

export async function loadPluginCatalog(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
  handle?: string | null
}) {
  const handle = input.handle?.trim()
  const result = await createPluginClient(input).plugin.catalog({
    directory: input.directory,
    ...(handle ? { handle } : {}),
  })
  return result.data?.items ?? []
}

export async function installPlugin(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
  plugin: string | CatalogItem
  handle?: string | null
  scope?: PluginInstallScope
}) {
  const next = typeof input.plugin === "string" ? input.plugin.trim() : input.plugin.spec.trim()
  const handle = input.handle?.trim()
  return createPluginClient(input).plugin.install({
    directory: input.directory,
    spec: next,
    ...(typeof input.plugin === "string" || input.plugin.source !== "hosted" || !input.plugin.hostedPluginId
      ? {}
      : { hostedPluginId: input.plugin.hostedPluginId }),
    ...(handle ? { handle } : {}),
    scope: input.scope ?? "project",
    approved: true,
  })
}

export async function removePlugin(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
  name: string
}) {
  return createPluginClient(input).plugin.remove({
    directory: input.directory,
    name: input.name,
  })
}

// ── File-defined plugins (.openanalyst/plugins/) ───────────────────────────────

export type FilePluginCatalogItem = {
  id: string
  name: string
  description?: string
  default: boolean
  installed: boolean
  enabled?: boolean
  // Core plugins (e.g. the Dashboard Canvas) are always installed and cannot be
  // uninstalled. Absent (undefined) is treated as removable for back-compat.
  removable?: boolean
  // "system" = a built-in OpenAnalyst app; "public" = a user-style plugin (e.g.
  // public-profile plugins). Drives the "built in" vs "public" tag in the manager.
  templateProfile?: "system" | "public"
}

type FilePluginMutationResult = { ok: true } | { ok: false; error?: string }
type FilePluginEnabledResult = { ok: true; id: string; enabled: boolean; changed: boolean } | { ok: false; error?: string }
type FileSetEnabledCall = (parameters: {
  directory: string
  id: string
  enabled: boolean
}) => Promise<{ data?: FilePluginEnabledResult }>
type FileCatalogCall = (parameters: { directory: string }) => Promise<{ data?: FilePluginCatalogItem[] }>
type FileWriteCall = (parameters: {
  directory: string
  path: string
  content: string
}) => Promise<{ data?: unknown }>
type RawPluginClient = {
  client?: {
    post?: <Data>(options: {
      url: string
      path?: Record<string, unknown>
      query?: Record<string, unknown>
      body?: unknown
      headers?: Record<string, string>
    }) => Promise<{ data?: Data }>
  }
}
const methodUnavailable = (err: unknown) => /\b405\b|method not allowed/i.test(err instanceof Error ? err.message : String(err))

export async function loadFilePluginCatalog(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
}): Promise<FilePluginCatalogItem[]> {
  const result = await createPluginClient(input).plugin.fileCatalog({ directory: input.directory })
  return (result.data ?? []) as FilePluginCatalogItem[]
}

export async function installFilePlugin(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
  id: string
}) {
  return createPluginClient(input).plugin.fileInstall({ directory: input.directory, id: input.id }) as Promise<{
    data?: FilePluginMutationResult
  }>
}

export async function uninstallFilePlugin(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
  id: string
}) {
  return createPluginClient(input).plugin.fileUninstall({ directory: input.directory, id: input.id }) as Promise<{
    data?: FilePluginMutationResult
  }>
}

// Download + extract the OA-App Builder kit into the active project's .openanalyst/oa-app-builder/
// (and surface its skill + agent). The route isn't in the generated client, so post it raw.
export async function installAppBuilderKit(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
}) {
  const client = createPluginClient(input)
  const rawPost = (client as unknown as RawPluginClient).client?.post
  if (!rawPost) throw new Error("OA-App Builder is unavailable in this OpenAnalyst client.")
  return await rawPost<{ ok: boolean; path?: string; error?: string }>({
    url: "/plugin/oa-app-builder/install",
    query: { directory: input.directory },
    headers: { "Content-Type": "application/json" },
  })
}

export async function setFilePluginEnabled(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
  id: string
  enabled: boolean
}) {
  const client = createPluginClient(input)
  const writeDisabledFile = async () => {
    const fileCatalog = (client.plugin as { fileCatalog?: FileCatalogCall }).fileCatalog
    const fileWrite = (client.file as { write?: FileWriteCall } | undefined)?.write
    if (!fileCatalog || !fileWrite) throw new Error("Plugin toggle fallback is unavailable in this OpenAnalyst client.")
    const result = await fileCatalog.call(client.plugin, { directory: input.directory })
    const disabled = new Set(
      (result.data ?? [])
        .filter((item) => item.installed && item.enabled === false)
        .map((item) => canonicalPluginAppID(item.id)),
    )
    const id = canonicalPluginAppID(input.id)
    if (input.enabled) disabled.delete(id)
    else disabled.add(id)
    await fileWrite.call(client.file, {
      directory: input.directory,
      path: ".openanalyst/plugins/.disabled.json",
      content: `${JSON.stringify({ ids: [...disabled].toSorted() }, null, 2)}\n`,
    })
    return { data: { ok: true, id: input.id, enabled: input.enabled, changed: true } satisfies FilePluginEnabledResult }
  }
  try {
    const fileSetEnabled = (client.plugin as { fileSetEnabled?: FileSetEnabledCall }).fileSetEnabled
    if (fileSetEnabled) {
      return await fileSetEnabled.call(client.plugin, {
        directory: input.directory,
        id: input.id,
        enabled: input.enabled,
      })
    }
    const rawPost = (client as unknown as RawPluginClient).client?.post
    if (!rawPost) throw new Error("Plugin toggle is unavailable in this OpenAnalyst client.")
    return await rawPost<FilePluginEnabledResult>({
      url: "/plugin/file-catalog/{id}/enabled",
      path: { id: input.id },
      query: { directory: input.directory },
      body: { enabled: input.enabled },
      headers: { "Content-Type": "application/json" },
    })
  } catch (err) {
    if (!methodUnavailable(err)) throw err
    return writeDisabledFile()
  }
}

export async function reloadPluginDirectory(input: {
  createClient: (config: { directory: string; throwOnError: true }) => OpenAnalystClient
  directory: string
  bootstrap: () => Promise<void>
}) {
  await createPluginClient(input).instance.dispose({ directory: input.directory })
  await input.bootstrap()
}

export function recommendedCatalogItem(input: { catalog: CatalogItem[]; hostedPluginId: string }) {
  return input.catalog.find((item) => item.source === "hosted" && item.hostedPluginId === input.hostedPluginId)
}
